<template>
	<div v-if="show" class="drawer-overlay" @click="closeDrawer">
		<div class="drawer notes-drawer" @click.stop>
			<div class="drawer-header">
				<h3>Customer Notes</h3>
				<button class="close-btn" @click="closeDrawer">✕</button>
			</div>
			<div class="drawer-content">
				<!-- Add New Note Form -->
				<div class="note-form">
					<div class="form-header" @click="formExpanded = !formExpanded">
						<h4>Add New Note</h4>
						<span class="toggle-icon" :class="{ expanded: formExpanded }">▼</span>
					</div>
					<div v-show="formExpanded" class="form-content">
						<div class="form-group">
							<label>Contact Date/Time:</label>
							<input
								v-model="newNote.contactDateTime"
								type="datetime-local"
								class="form-input"
							/>
						</div>
						<div class="form-group">
							<label>Method:</label>
							<select v-model="newNote.method" class="form-input">
								<option v-for="method in methodOptions" :key="method" :value="method">
									{{ method }}
								</option>
							</select>
							<input
								v-if="newNote.method === 'other'"
								v-model="newNote.customMethod"
								type="text"
								class="form-input"
								placeholder="Enter custom method..."
								style="margin-top: 8px;"
							/>
						</div>
						<div class="form-group">
							<label>Reason:</label>
							<select v-model="newNote.reason" class="form-input">
								<option v-for="reason in reasonOptions" :key="reason" :value="reason">
									{{ reason }}
								</option>
							</select>
							<input
								v-if="newNote.reason === 'other'"
								v-model="newNote.customReason"
								type="text"
								class="form-input"
								placeholder="Enter custom reason..."
								style="margin-top: 8px;"
							/>
						</div>
						<div class="form-group">
							<label>Note:</label>
							<textarea
								v-model="newNote.note"
								class="form-textarea"
								rows="4"
								placeholder="Enter your note here..."
							></textarea>
						</div>
						<button class="action-btn primary" @click="addNote">
							Add Note
						</button>
					</div>
				</div>

				<!-- Notes List -->
				<div class="notes-list">
					<h4 v-if="notes.length > 0">Existing Notes ({{ notes.length }})</h4>
					<div v-if="loading" class="loading">Loading notes...</div>
					<div v-else-if="notes.length > 0">
						<div v-for="(note, index) in notes" :key="note.id || index" class="note-item">
							<div class="note-meta">
								<div class="meta-row">
									<span class="meta-label">Date:</span>
									<span class="meta-value">{{ formatDateTime(note.createDateTime) }}</span>
								</div>
								<div class="meta-row">
									<span class="meta-label">Contact:</span>
									<span class="meta-value">{{ note.method }}</span>
								</div>
								<div class="meta-row">
									<span class="meta-label">Reason:</span>
									<span class="meta-value">{{ note.reason }}</span>
								</div>
								<div v-if="getCreatedByEmail(note)" class="meta-row">
									<span class="meta-label">By:</span>
									<span class="meta-value">{{ getCreatedByEmail(note) }}</span>
								</div>
							</div>
							<div class="note-text">{{ note.note }}</div>
						</div>
					</div>
					<div v-else class="empty">No notes yet</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'CustomerNotesDrawer',
	props: {
		show: {
			type: Boolean,
			required: true
		},
		notes: {
			type: Array,
			default: () => []
		},
		loading: {
			type: Boolean,
			default: false
		},
		methodOptions: {
			type: Array,
			default: () => ['zendesk', 'phone', 'email', 'in-person', 'chat', 'other']
		},
		reasonOptions: {
			type: Array,
			default: () => ['inquiry', 'complaint', 'follow-up', 'support', 'other']
		},
		customers: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			formExpanded: false,
			newNote: {
				method: 'phone',
				reason: 'inquiry',
				note: '',
				contactDateTime: '',
				customMethod: '',
				customReason: ''
			}
		};
	},
	methods: {
		getCreatedByEmail(note) {
			if (note.addedByCustomerID && this.customers && this.customers.length > 0) {
				const creator = this.customers.find(c => c.id === note.addedByCustomerID);
				if (creator && creator.person && creator.person.email) {
					return creator.person.email;
				}
			}

			return null;
		},
		closeDrawer() {
			this.$emit('close');
		},
		addNote() {
			if (!this.newNote.note.trim()) {
				this.$emit('error', 'Please enter a note');
				return;
			}

			if (this.newNote.method === 'other' && !this.newNote.customMethod.trim()) {
				this.$emit('error', 'Please enter a custom method');
				return;
			}

			if (this.newNote.reason === 'other' && !this.newNote.customReason.trim()) {
				this.$emit('error', 'Please enter a custom reason');
				return;
			}

			const noteData = {
				contactDateTime: new Date(this.newNote.contactDateTime).toISOString(),
				method: this.newNote.method === 'other' ? this.newNote.customMethod : this.newNote.method,
				reason: this.newNote.reason === 'other' ? this.newNote.customReason : this.newNote.reason,
				note: this.newNote.note
			};

			this.$emit('add-note', noteData);
			this.resetForm();
		},
		resetForm() {
			const now = new Date();
			const isoString = now.toISOString();
			const localDateTime = isoString.slice(0, 16);
			this.newNote = {
				method: 'phone',
				reason: 'inquiry',
				note: '',
				contactDateTime: localDateTime,
				customMethod: '',
				customReason: ''
			};
		},
		formatDateTime(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleString('en-GB', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			});
		},
		initializeForm() {
			const now = new Date();
			const isoString = now.toISOString();
			const localDateTime = isoString.slice(0, 16);
			this.newNote.contactDateTime = localDateTime;
		}
	},
	watch: {
		show(newVal) {
			if (newVal) {
				this.initializeForm();
			}
		}
	}
};
</script>

<style scoped>
/* Drawer Overlay */
.drawer-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: flex-end;
	z-index: 1000;
	animation: fadeIn 0.2s ease;
}

.drawer {
	background-color: var(--admin-bg-secondary);
	width: 500px;
	height: 100vh;
	display: flex;
	flex-direction: column;
	box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
	animation: slideIn 0.3s ease;
}

.drawer.notes-drawer {
	width: 500px;
}

@keyframes slideIn {
	from { transform: translateX(100%); }
	to { transform: translateX(0); }
}

@keyframes fadeIn {
	from { opacity: 0; }
	to { opacity: 1; }
}

.drawer-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid var(--admin-border);
}

.drawer-header h3 {
	margin: 0;
	font-size: 18px;
	color: var(--admin-text-primary);
}

.close-btn {
	background: none;
	border: none;
	font-size: 24px;
	cursor: pointer;
	color: var(--admin-text-secondary);
	padding: 0;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: var(--border-radius-s);
	transition: all 0.2s ease;
}

.close-btn:hover {
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
}

.drawer-content {
	flex: 1;
	padding: 20px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

/* Note Form */
.note-form {
	background-color: var(--admin-hover-bg);
	padding: 0;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
}

.form-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s ease;
}

.form-header:hover {
	background-color: var(--admin-border);
}

.form-header h4 {
	margin: 0;
	font-size: 14px;
	color: var(--admin-text-primary);
	font-weight: 600;
}

.toggle-icon {
	color: var(--admin-text-secondary);
	font-size: 12px;
	transition: transform 0.3s ease;
	display: flex;
	align-items: center;
}

.toggle-icon.expanded {
	transform: rotate(180deg);
}

.form-content {
	padding: 0 15px 15px 15px;
}

.note-form .form-group {
	margin-bottom: 12px;
}

.note-form .form-group label {
	display: block;
	font-size: 12px;
	font-weight: 500;
	color: var(--admin-text-primary);
	margin-bottom: 6px;
}

.note-form input[type="datetime-local"],
.note-form select,
.note-form textarea {
	width: 100%;
	padding: 8px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 12px;
	font-family: inherit;
}

.note-form textarea {
	resize: vertical;
	min-height: 80px;
}

.note-form .action-btn {
	width: 100%;
	margin-top: 10px;
}

/* Notes List */
.notes-list {
	flex: 1;
	overflow-y: auto;
}

.notes-list h4 {
	margin-top: 0;
	margin-bottom: 12px;
	font-size: 14px;
	color: var(--admin-text-primary);
	font-weight: 600;
}

.notes-list .loading,
.notes-list .empty {
	text-align: center;
	color: var(--admin-text-secondary);
	padding: 20px;
	font-size: 13px;
}

/* Note Item */
.note-item {
	background-color: var(--admin-hover-bg);
	border: 1px solid var(--admin-border);
	padding: 12px;
	border-radius: var(--border-radius-s);
	margin-bottom: 10px;
}

.note-meta {
	margin-bottom: 10px;
}

.meta-row {
	display: flex;
	gap: 8px;
	font-size: 12px;
	margin-bottom: 6px;
}

.meta-row:last-child {
	margin-bottom: 0;
}

.meta-label {
	color: var(--admin-text-secondary);
	font-weight: 500;
	min-width: 70px;
}

.meta-value {
	color: var(--admin-text-primary);
	word-break: break-word;
}

.note-text {
	font-size: 13px;
	color: var(--admin-text-primary);
	line-height: 1.4;
	white-space: pre-wrap;
	word-break: break-word;
}

/* Responsive */
@media (max-width: 768px) {
	.drawer,
	.drawer.notes-drawer {
		width: 100%;
	}

	.drawer-header {
		padding-top: 50px;
	}
}
</style>
